import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, CheckCircle, Gift } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

const RegistrationSuccess: React.FC = () => {
    const { t } = useTranslation();
    const { isRTL } = useLanguage();
    const location = useLocation();
    const [isEarlyAdopter, setIsEarlyAdopter] = useState(false);

    // Vérifier si l'utilisateur est un Early Adopter
    useEffect(() => {
        const earlyAdopterStatus = localStorage.getItem('isEarlyAdopter') === 'true' ||
            (location.state && location.state.isEarlyAdopter);

        setIsEarlyAdopter(earlyAdopterStatus);

        // Nettoyer le localStorage après usage
        localStorage.removeItem('isEarlyAdopter');
    }, [location]);

    return (
        <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8 ${isRTL ? 'rtl' : ''}`}>
            <div className="max-w-3xl mx-auto">
                <div className="bg-white rounded-xl shadow-xl px-8 py-12 text-center">
                    <Shield className="h-16 w-16 text-[#115DF2] mx-auto" />
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mt-4" />

                    {isEarlyAdopter && (
                        <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-800">
                            <Gift className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-semibold">{t('registrationSuccess.earlyAdopter.badge')}</span>
                        </div>
                    )}

                    <h1 className="mt-6 text-3xl font-bold text-gray-900">
                        {isEarlyAdopter
                            ? t('registrationSuccess.earlyAdopter.title')
                            : t('registrationSuccess.regular.title')}
                    </h1>

                    <p className="mt-4 text-lg text-gray-600">
                        {isEarlyAdopter
                            ? t('registrationSuccess.earlyAdopter.description')
                            : t('registrationSuccess.regular.description')}
                    </p>

                    <p className="mt-4 text-gray-600">
                        {t('registrationSuccess.emailConfirmation')}
                    </p>

                    {isEarlyAdopter && (
                        <div className="mt-6 p-4 bg-blue-50 rounded-lg text-left">
                            <h3 className="font-medium text-[#115DF2]">
                                {t('registrationSuccess.earlyAdopter.benefits.title')}
                            </h3>
                            <ul className="mt-2 space-y-2 text-gray-600">
                                <li className="flex items-start">
                                    <svg className={`h-5 w-5 text-green-500 ${isRTL ? 'ml-2' : 'mr-2'} flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('registrationSuccess.earlyAdopter.benefits.freeAccess')}
                                </li>
                                <li className="flex items-start">
                                    <svg className={`h-5 w-5 text-green-500 ${isRTL ? 'ml-2' : 'mr-2'} flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('registrationSuccess.earlyAdopter.benefits.standardCredits')}
                                </li>
                                <li className="flex items-start">
                                    <svg className={`h-5 w-5 text-green-500 ${isRTL ? 'ml-2' : 'mr-2'} flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('registrationSuccess.earlyAdopter.benefits.aiCredits')}
                                </li>
                                <li className="flex items-start">
                                    <svg className={`h-5 w-5 text-green-500 ${isRTL ? 'ml-2' : 'mr-2'} flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('registrationSuccess.earlyAdopter.benefits.prioritySupport')}
                                </li>
                            </ul>
                        </div>
                    )}

                    <div className="mt-8">
                        <Link
                            to="/login"
                            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-[#115DF2] hover:bg-[#0E4BC2]"
                        >
                            {t('registrationSuccess.actions.login')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegistrationSuccess;